import { ErrorBoundary } from 'components/Errors/ErrorBoundary';
import LoadingScreen from 'components/LoadingScreen';
import { ModuleGuard } from 'components/ModuleGuard';
import PageLayout from 'components/PageLayout';
import ActiveFiltersBar from 'components/SearchAndFilters/SelectedFiltersBar';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { Suspense } from 'react';
import { ToastContainer } from 'react-toastify';
import { CardTitle, Col, Row } from 'reactstrap';
import BPAGuardFallback from 'views/Admin/Dashboard/components/BPA/BPAComingSoon';

import BulkActions from './BulkActions';
import BPAFindingsDataGrid from './components/BPAFindingsDataGrid/BPAFindingsDataGrid';
import FindingsInsights from './components/FindingsInsights/FindingsInsights';
import Filters from './Filters';
import { useFilterConfig } from './Filters.config';
import { useBPAFindingsInsights } from './hooks/useBPAFindingsInsights';
import {
  findingsDataAtom,
  filterValuesAtom,
  useOnFilterChangeResetRelatedAtoms,
} from './state';

const SelectedFilters: React.FC<{
  filtersConfig: ReturnType<typeof useFilterConfig>;
}> = ({ filtersConfig }) => {
  const setFilterValues = useSetAtom(filterValuesAtom);
  const resetOnFilterChangeAtoms = useOnFilterChangeResetRelatedAtoms();

  return (
    <Suspense fallback={<></>}>
      <ActiveFiltersBar
        filtersConfig={filtersConfig}
        onFiltersReset={() => {
          setFilterValues({ ...filtersConfig.getActiveFilterValues() });
          resetOnFilterChangeAtoms();
        }}
      />
    </Suspense>
  );
};

const ListingCount: React.FC = () => {
  const listingData = useAtomValue(findingsDataAtom);
  return <>{listingData?.loading ? '...' : listingData.count}</>;
};

const BPAFindings: React.FC = () => {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <div className="content">
        <ErrorBoundary>
          <BPAFindingsListing />
        </ErrorBoundary>
      </div>
      <ToastContainer />
    </Suspense>
  );
};

function BPAFindingsListing() {
  const filtersConfig = useFilterConfig();

  // Orchestrate the insights fetch at page level so it stays alive regardless
  // of how the presentational insights bar is mounted/unmounted.
  useBPAFindingsInsights();

  return (
    <ModuleGuard module="bpa" fallback={<BPAGuardFallback />}>
      <PageLayout
        heading={
          <Row>
            <Col md="auto" sm="12">
              <CardTitle>
                <h4>
                  BPA Findings
                  <label id="counts" className="label btn">
                    <Suspense fallback={<>...</>}>
                      <ListingCount />
                    </Suspense>
                  </label>
                </h4>
              </CardTitle>
            </Col>
          </Row>
        }
        subheading="Identify and manage security best practice findings."
        filteringOptions={<Filters filtersConfig={filtersConfig} />}
      >
        <Suspense fallback={<LoadingScreen />}>
          <div className="container-fluid p-0">
            {/* Selected Filters and Bulk Actions Row */}
            <div className="row mb-1">
              {/* Expanding Column - Filter Badges */}
              <div className="col">
                <SelectedFilters filtersConfig={filtersConfig} />
              </div>

              {/* Fixed Column with Right-Aligned Bulk Actions */}
              <div className="col-auto text-right align-baseline">
                <Suspense fallback={<>...</>}>
                  <BulkActions />
                </Suspense>
              </div>
            </div>

            {/* Summary Insights */}
            <FindingsInsights />

            {/* Data Grid */}
            <BPAFindingsDataGrid />
          </div>
        </Suspense>
      </PageLayout>
    </ModuleGuard>
  );
}

export default BPAFindings;
